import type { Row } from "@rocicorp/zero";
import { styled } from "goober";
import { useEffect, useState } from "react";
import { config } from "../environment";
import { apiDocsPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";
import { useIntercomMessenger } from "../hooks/useIntercomMessenger";
import { useLogout } from "../hooks/useLogout";
import { isEditableTarget } from "../lib/isEditableTarget";
import { openSupportUrl } from "../lib/support";
import { makeUrl, routes } from "../lib/urls";
import { Avatar } from "./Avatar";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { KeyboardShortcutsPanel } from "./KeyboardShortcutsPanel";
import { Keycap } from "./Keycap";
import { Text } from "./Text";

type Props = {
  user: Row["user"];
};

const CHOOSE_TENANT_URL = "https://login.tines.com/saml_idp/choose_tenant";

const AvatarButton = styled("button")`
  position: relative;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: var(--alpha4);
  }
`;

const UnreadDot = styled("span")`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--red-600);
`;

const AvatarUnreadDot = styled(UnreadDot)`
  position: absolute;
  top: 1px;
  right: 1px;
  border: 1.5px solid var(--surface-secondary);
`;

export function ProfileMenu({ user }: Props) {
  const logout = useLogout();
  const externalApiUrl = apiUrl();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const {
    unreadCount,
    failed: intercomFailed,
    show: showIntercom,
  } = useIntercomMessenger();
  const intercomUnread = unreadCount > 0;
  const intercomEnabled = Boolean(config.intercomAppId);
  const intercomUsable = intercomEnabled && !intercomFailed;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMetaSlash = (e.metaKey || e.ctrlKey) && e.key === "/";
      const isQuestionMark = e.key === "?" && !isEditableTarget(e);
      if (isMetaSlash || isQuestionMark) {
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <Dropdown
        opener={
          <AvatarButton type="button">
            <Avatar size={24} user={user} tooltip={false} />
            {intercomUnread && <AvatarUnreadDot />}
          </AvatarButton>
        }
        sections={[
          {
            items: [
              {
                type: "link" as const,
                left: <Icon name="circle-user-round" size={16} />,
                label: "Profile",
                to: makeUrl(routes.modal.member, { id: user.id }),
              },
              {
                type: "link" as const,
                left: <Icon name="settings-2" size={16} />,
                label: "Settings",
                to: makeUrl(routes.settings),
                right: (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Keycap style={{ padding: "2px 4px" }}>
                      <Text variant="text-smaller-md">G</Text>
                    </Keycap>
                    <Text
                      variant="text-smaller-md"
                      color="var(--text-secondary)"
                    >
                      then
                    </Text>
                    <Keycap style={{ padding: "2px 4px" }}>
                      <Text variant="text-smaller-md">S</Text>
                    </Keycap>
                  </div>
                ),
              },
              {
                type: "button",
                left: <Icon name="book" size={16} />,
                label: "API docs",
                right: (
                  <Icon
                    name="arrow-up-right"
                    size={12}
                    color="var(--text-secondary)"
                  />
                ),
                onClick: () =>
                  window.open(
                    `${externalApiUrl}${apiDocsPath}`,
                    "_blank",
                    "noopener,noreferrer"
                  ),
              },
              {
                type: "button",
                left: <Icon name="keyboard" size={16} />,
                label: "Shortcuts and tips",
                right: (
                  <div style={{ display: "flex", gap: 2 }}>
                    <Keycap style={{ padding: "2px 4px" }}>
                      <Text variant="text-smaller-md">⌘</Text>
                    </Keycap>
                    <Keycap style={{ padding: "2px 4px" }}>
                      <Text variant="text-smaller-md">/</Text>
                    </Keycap>
                  </div>
                ),
                onClick: () => setShortcutsOpen(true),
              },
              {
                type: "button" as const,
                left: <Icon name="message-circle-question-mark" size={16} />,
                label: "Support",
                ...(intercomUsable
                  ? {
                      right: intercomUnread ? <UnreadDot /> : undefined,
                      onClick: showIntercom,
                    }
                  : {
                      right: (
                        <Icon
                          name="arrow-up-right"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ),
                      onClick: openSupportUrl,
                    }),
              },
            ],
          },
          {
            items: [
              {
                type: "link",
                left: <Icon name="orbit" size={16} />,
                label: "Switch tenant",
                to: CHOOSE_TENANT_URL,
              },
              {
                type: "button",
                left: <Icon name="output" size={16} />,
                label: "Log out",
                onClick: logout,
              },
            ],
          },
        ]}
      />
      <KeyboardShortcutsPanel
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
    </>
  );
}
